import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  X, Plus, Trash2, GripVertical, ChevronDown, ChevronUp,
  Layers, Save, AlertCircle, CheckCircle2, Info, Grid3x3, MoveVertical
} from 'lucide-react';
import { fetchApartmentUnits, fetchUnitGroups, saveUnitGroups } from './api';
import { useAppTheme } from '../../contexts/ThemeContext';

const GroupUnitsDialog = ({ currentApartment, onClose, onSaved }) => {
  const { palette, Colors } = useAppTheme();

  const [allUnits, setAllUnits]       = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [saving, setSaving]           = useState(false);
  const [apiError, setApiError]       = useState(null);
  const [successMsg, setSuccessMsg]   = useState(null);

  // flat list of groups (cross-category)
  // shape: { _key, id?, catId, name, order, unitIds: number[] }
  const [groups, setGroups]           = useState([]);

  // which group checkbox is currently active for quick-add
  const [activeGroupKey, setActiveGroupKey] = useState(null);

  // insert-at-position dialog
  const [insertDialog, setInsertDialog] = useState(null);

  const P   = Colors.primary.main;
  const bg  = palette.background;
  const txt = palette.text;
  const bdr = palette.border;

  const genKey = () => Math.random().toString(36).slice(2);

  // ── derived ──────────────────────────────────────────────────────────────
  const allAssignedIds  = new Set(groups.flatMap(g => g.unitIds));
  const unassignedUnits = allUnits.filter(u => !allAssignedIds.has(u.id));

  // ── load ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      try {
        setLoadingData(true);
        const units = await fetchApartmentUnits(currentApartment.id);
        setAllUnits(units);

        const allGroups = [];
        await Promise.all(
          (currentApartment.categories || []).map(async (cat) => {
            try {
              const grps = await fetchUnitGroups(cat.id);
              grps.forEach(g => {
                allGroups.push({
                  id:      g.id,
                  catId:   cat.id,
                  name:    g.name,
                  order:   g.order,
                  unitIds: (g.units || []).map(u => u.id),
                  _key:    String(g.id),
                });
              });
            } catch (_) {}
          })
        );
        allGroups.sort((a, b) => a.order - b.order || a.id - b.id);
        setGroups(allGroups);
        if (allGroups.length > 0) setActiveGroupKey(allGroups[0]._key);
      } catch (err) {
        setApiError('Failed to load data: ' + err.message);
      } finally {
        setLoadingData(false);
      }
    };
    load();
  }, [currentApartment.id]);

  // ── group mutations ───────────────────────────────────────────────────────
  const addGroup = () => {
    const key = genKey();
    const defaultCatId = currentApartment.categories?.[0]?.id;
    setGroups(prev => [...prev, { name: '', order: prev.length, unitIds: [], _key: key, catId: defaultCatId }]);
    setActiveGroupKey(key);
  };

  const removeGroup = (key) => {
    setGroups(prev => prev.filter(g => g._key !== key));
    if (activeGroupKey === key) setActiveGroupKey(null);
  };

  const updateGroupName = (key, name) =>
    setGroups(prev => prev.map(g => g._key === key ? { ...g, name } : g));

  const moveGroup = (key, dir) => {
    setGroups(prev => {
      const list = [...prev];
      const idx  = list.findIndex(g => g._key === key);
      const swap = dir === 'up' ? idx - 1 : idx + 1;
      if (swap < 0 || swap >= list.length) return prev;
      [list[idx], list[swap]] = [list[swap], list[idx]];
      return list.map((g, i) => ({ ...g, order: i }));
    });
  };

  // ── unit assignment ───────────────────────────────────────────────────────
  const assignUnit = (unitId) => {
    if (!activeGroupKey) return;
    setGroups(prev => {
      const cleaned = prev.map(g => ({ ...g, unitIds: g.unitIds.filter(id => id !== unitId) }));
      return cleaned.map(g =>
        g._key === activeGroupKey ? { ...g, unitIds: [...g.unitIds, unitId] } : g
      );
    });
  };

  const removeUnitFromGroup = (groupKey, unitId) => {
    setGroups(prev =>
      prev.map(g =>
        g._key === groupKey ? { ...g, unitIds: g.unitIds.filter(id => id !== unitId) } : g
      )
    );
  };

  const moveUnit = (groupKey, unitId, dir) => {
    setGroups(prev =>
      prev.map(g => {
        if (g._key !== groupKey) return g;
        const ids  = [...g.unitIds];
        const idx  = ids.indexOf(unitId);
        const swap = dir === 'up' ? idx - 1 : idx + 1;
        if (swap < 0 || swap >= ids.length) return g;
        [ids[idx], ids[swap]] = [ids[swap], ids[idx]];
        return { ...g, unitIds: ids };
      })
    );
  };

  const insertUnitAtPosition = (unitId, targetGroupKey, position) => {
    setGroups(prev => {
      const cleaned = prev.map(g => ({ ...g, unitIds: g.unitIds.filter(id => id !== unitId) }));
      return cleaned.map(g => {
        if (g._key !== targetGroupKey) return g;
        const ids = [...g.unitIds];
        ids.splice(Math.max(0, Math.min(position, ids.length)), 0, unitId);
        return { ...g, unitIds: ids };
      });
    });
    setInsertDialog(null);
  };

  const clearGroup = (key) =>
    setGroups(prev => prev.map(g => g._key === key ? { ...g, unitIds: [] } : g));

  // ── save ─────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    for (const g of groups) {
      if (!g.name.trim()) { setApiError('All groups must have a name before saving.'); return; }
    }
    try {
      setSaving(true);
      setApiError(null);

      // Group by catId for the backend endpoint
      const byCat = {};
      groups.forEach((g, idx) => {
        const catId = g.catId || currentApartment.categories?.[0]?.id;
        if (!byCat[catId]) byCat[catId] = [];
        byCat[catId].push({ ...(g.id ? { id: g.id } : {}), name: g.name.trim(), order: g.order ?? idx, unitIds: g.unitIds });
      });

      await Promise.all(
        Object.entries(byCat).map(([catId, grps]) => saveUnitGroups(Number(catId), grps))
      );

      setSuccessMsg('Groups saved successfully!');
      setTimeout(() => onSaved(), 1000);
    } catch (err) {
      setApiError('Failed to save: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  // ── helpers ───────────────────────────────────────────────────────────────
  const unitById  = (id) => allUnits.find(u => u.id === id);
  const unitLabel = (u)  => u ? `${u.number} (${u.categoryName})` : '';

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-KE', { style: 'currency', currency: 'KES', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount);

  const inputSt = {
    padding: '7px 12px', border: `1px solid ${bdr}`, borderRadius: '8px',
    outline: 'none', backgroundColor: bg.tertiary, color: txt.primary, fontSize: '14px', width: '100%',
  };

  // ── render ────────────────────────────────────────────────────────────────
  return createPortal(
    <div className="fixed inset-0 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.65)', zIndex: 9999 }}>
      <div className="flex flex-col rounded-2xl shadow-2xl w-full max-w-4xl"
        style={{ backgroundColor: bg.secondary, border: `1px solid ${bdr}`, maxHeight: 'calc(100vh - 32px)' }}>

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 rounded-t-2xl"
          style={{ borderBottom: `1px solid ${bdr}`, backgroundColor: bg.tertiary }}>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg" style={{ backgroundColor: `${Colors.accent.purple}20` }}>
              <Layers className="w-5 h-5" style={{ color: Colors.accent.purple }} />
            </div>
            <div>
              <h2 className="text-lg font-bold" style={{ color: txt.primary }}>Group Units</h2>
              <p className="text-xs" style={{ color: txt.secondary }}>{currentApartment.name}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg transition" style={{ color: txt.muted }}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = bg.primary}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
            <X className="w-5 h-5" />
          </button>
        </div>

        {loadingData ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin mb-3"
              style={{ borderColor: P, borderTopColor: 'transparent' }} />
            <p style={{ color: txt.secondary }}>Loading units &amp; groups…</p>
          </div>
        ) : (
          <div className="flex flex-col flex-1" style={{ overflow: 'hidden', minHeight: 0 }}>

            {/* Info banner */}
            <div className="mx-6 mt-4 px-4 py-3 rounded-lg flex items-start gap-2"
              style={{ backgroundColor: `${P}08`, border: `1px solid ${P}25` }}>
              <Info className="w-4 h-4 mt-0.5 shrink-0" style={{ color: P }} />
              <p className="text-xs leading-relaxed" style={{ color: txt.secondary }}>
                <strong>Check</strong> a group to activate it, then click any unit on the right to add it instantly.
                Units show their category in brackets. Use the <MoveVertical className="w-3 h-3 inline" /> button on any assigned unit to insert a skipped unit at a specific position.
              </p>
            </div>

            {/* Body */}
            <div className="flex gap-0 flex-1 overflow-hidden mt-4 mx-6 mb-4 rounded-xl"
              style={{ border: `1px solid ${bdr}` }}>

              {/* LEFT: groups */}
              <div className="flex flex-col flex-1 overflow-hidden" style={{ borderRight: `1px solid ${bdr}` }}>
                <div className="flex items-center justify-between px-4 py-3"
                  style={{ borderBottom: `1px solid ${bdr}`, backgroundColor: bg.tertiary }}>
                  <span className="text-sm font-semibold" style={{ color: txt.primary }}>
                    Groups ({groups.length})
                  </span>
                  <button onClick={addGroup}
                    className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg transition"
                    style={{ backgroundColor: `${P}15`, color: P }}>
                    <Plus className="w-3.5 h-3.5" /> New Group
                  </button>
                </div>

                <div className="overflow-y-auto flex-1 p-3 space-y-3">
                  {groups.length === 0 && (
                    <div className="text-center py-10">
                      <Grid3x3 className="w-10 h-10 mx-auto mb-2" style={{ color: txt.muted }} />
                      <p className="text-sm font-medium" style={{ color: txt.primary }}>No groups yet</p>
                      <p className="text-xs mt-1" style={{ color: txt.secondary }}>
                        Click <strong>New Group</strong> to get started
                      </p>
                    </div>
                  )}

                  {groups.map((group, gIdx) => (
                    <GroupCard
                      key={group._key}
                      group={group}
                      gIdx={gIdx}
                      totalGroups={groups.length}
                      isActive={activeGroupKey === group._key}
                      onCheckboxChange={() => setActiveGroupKey(prev => prev === group._key ? null : group._key)}
                      unitById={unitById}
                      unitLabel={unitLabel}
                      formatCurrency={formatCurrency}
                      onNameChange={(name) => updateGroupName(group._key, name)}
                      onMoveUp={() => moveGroup(group._key, 'up')}
                      onMoveDown={() => moveGroup(group._key, 'down')}
                      onRemove={() => removeGroup(group._key)}
                      onMoveUnit={(uid, dir) => moveUnit(group._key, uid, dir)}
                      onRemoveUnit={(uid) => removeUnitFromGroup(group._key, uid)}
                      onClearAll={() => clearGroup(group._key)}
                      onInsertRequest={(uid) => setInsertDialog({ unitId: uid, label: unitLabel(unitById(uid)) })}
                      Colors={Colors} bdr={bdr} bg={bg} txt={txt} P={P} inputSt={inputSt}
                    />
                  ))}
                </div>
              </div>

              {/* RIGHT: all units pool */}
              <div className="flex flex-col w-60 overflow-hidden">
                <div className="px-4 py-3" style={{ borderBottom: `1px solid ${bdr}`, backgroundColor: bg.tertiary }}>
                  <p className="text-sm font-semibold" style={{ color: txt.primary }}>
                    All Units ({unassignedUnits.length} unassigned)
                  </p>
                  {activeGroupKey ? (
                    <p className="text-xs mt-0.5 truncate" style={{ color: Colors.accent.purple }}>
                      → <strong>{groups.find(g => g._key === activeGroupKey)?.name || 'group'}</strong>
                    </p>
                  ) : (
                    <p className="text-xs mt-0.5" style={{ color: txt.muted }}>Check a group to start adding</p>
                  )}
                </div>
                <div className="overflow-y-auto flex-1 p-2 space-y-1">
                  {unassignedUnits.length === 0 && (
                    <div className="text-center py-6">
                      <CheckCircle2 className="w-6 h-6 mx-auto mb-1" style={{ color: Colors.status.success }} />
                      <p className="text-xs" style={{ color: txt.secondary }}>All units assigned!</p>
                    </div>
                  )}
                  {unassignedUnits.map(unit => (
                    <button key={unit.id}
                      onClick={() => assignUnit(unit.id)}
                      disabled={!activeGroupKey}
                      className="w-full text-left px-3 py-2 rounded-lg text-xs font-medium transition flex items-center justify-between disabled:opacity-40 disabled:cursor-not-allowed"
                      style={{ backgroundColor: bg.secondary, border: `1px solid ${bdr}`, color: txt.primary }}
                      onMouseEnter={e => { if (activeGroupKey) e.currentTarget.style.backgroundColor = `${P}12`; }}
                      onMouseLeave={e => e.currentTarget.style.backgroundColor = bg.secondary}>
                      <span>
                        {unit.number}{' '}
                        <span style={{ color: txt.muted }}>({unit.categoryName})</span>
                      </span>
                      <Plus className="w-3 h-3 shrink-0" style={{ color: activeGroupKey ? P : txt.muted }} />
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 rounded-b-2xl"
          style={{ borderTop: `1px solid ${bdr}`, backgroundColor: bg.tertiary }}>
          <div className="flex-1">
            {apiError && (
              <div className="flex items-center gap-2 text-sm" style={{ color: Colors.status.error }}>
                <AlertCircle className="w-4 h-4 shrink-0" />{apiError}
              </div>
            )}
            {successMsg && (
              <div className="flex items-center gap-2 text-sm" style={{ color: Colors.status.success }}>
                <CheckCircle2 className="w-4 h-4 shrink-0" />{successMsg}
              </div>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button onClick={onClose} disabled={saving}
              className="px-5 py-2 rounded-lg font-semibold text-sm transition"
              style={{ border: `1px solid ${bdr}`, color: txt.primary, backgroundColor: bg.secondary }}>
              Cancel
            </button>
            <button onClick={handleSave} disabled={saving || loadingData}
              className="flex items-center gap-2 px-5 py-2 rounded-lg font-semibold text-sm text-white transition shadow-sm disabled:opacity-50"
              style={{ backgroundColor: Colors.accent.purple }}>
              <Save className="w-4 h-4" />
              {saving ? 'Saving…' : 'Save Groups'}
            </button>
          </div>
        </div>
      </div>

      {/* Insert-at-position sub-dialog */}
      {insertDialog && (
        <InsertPositionDialog
          unitId={insertDialog.unitId}
          unitLabel={insertDialog.label}
          groups={groups}
          onInsert={insertUnitAtPosition}
          onClose={() => setInsertDialog(null)}
          bg={bg} txt={txt} bdr={bdr} P={P} Colors={Colors}
        />
      )}
    </div>
  , document.body);
};


// ─────────────────────────────────────────────────────────────────────────────
// GroupCard
// ─────────────────────────────────────────────────────────────────────────────
const GroupCard = ({
  group, gIdx, totalGroups, isActive, onCheckboxChange,
  unitById, unitLabel, formatCurrency,
  onNameChange, onMoveUp, onMoveDown, onRemove,
  onMoveUnit, onRemoveUnit, onClearAll, onInsertRequest,
  Colors, bdr, bg, txt, P, inputSt,
}) => {
  const [collapsed, setCollapsed] = useState(false);

  return (
    <div className="rounded-xl overflow-hidden"
      style={{
        border: `2px solid ${isActive ? Colors.accent.purple : bdr}`,
        backgroundColor: bg.primary,
        transition: 'border-color 0.2s',
      }}>

      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2.5"
        style={{
          backgroundColor: isActive ? `${Colors.accent.purple}12` : `${Colors.accent.purple}06`,
          borderBottom: collapsed ? 'none' : `1px solid ${bdr}`,
        }}>

        {/* Checkbox to activate */}
        <input
          type="checkbox"
          checked={isActive}
          onChange={onCheckboxChange}
          title="Check to make this the active group"
          style={{ width: '15px', height: '15px', accentColor: Colors.accent.purple, cursor: 'pointer', flexShrink: 0 }}
        />

        {/* order arrows */}
        <div className="flex flex-col">
          <button onClick={onMoveUp} disabled={gIdx === 0} className="disabled:opacity-30 p-0.5">
            <ChevronUp className="w-3.5 h-3.5" style={{ color: Colors.accent.purple }} />
          </button>
          <button onClick={onMoveDown} disabled={gIdx === totalGroups - 1} className="disabled:opacity-30 p-0.5">
            <ChevronDown className="w-3.5 h-3.5" style={{ color: Colors.accent.purple }} />
          </button>
        </div>

        <span className="text-xs font-bold px-1.5 py-0.5 rounded"
          style={{ backgroundColor: `${Colors.accent.purple}25`, color: Colors.accent.purple, minWidth: '24px', textAlign: 'center' }}>
          {gIdx + 1}
        </span>

        <input type="text" value={group.name} onChange={e => onNameChange(e.target.value)}
          placeholder="Group name, e.g. Ground Floor"
          style={{ ...inputSt, flex: 1, padding: '5px 10px', fontSize: '13px' }}
          onClick={e => e.stopPropagation()} />

        <span className="text-xs px-2 py-0.5 rounded-full font-semibold"
          style={{ backgroundColor: `${P}20`, color: P }}>
          {group.unitIds.length} unit{group.unitIds.length !== 1 ? 's' : ''}
        </span>

        <button onClick={() => setCollapsed(c => !c)} className="p-1 rounded" style={{ color: txt.muted }}>
          {collapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
        </button>

        <button onClick={onRemove} className="p-1 rounded transition" style={{ color: Colors.status.error }}
          onMouseEnter={e => e.currentTarget.style.backgroundColor = `${Colors.status.error}15`}
          onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      {/* Body */}
      {!collapsed && (
        <div className="p-2">
          {group.unitIds.length > 0 && (
            <div className="flex gap-2 mb-2">
              <button onClick={onClearAll}
                className="text-xs px-2.5 py-1 rounded-lg font-medium"
                style={{ backgroundColor: `${Colors.status.error}12`, color: Colors.status.error }}>
                Clear all
              </button>
            </div>
          )}

          {group.unitIds.length === 0 ? (
            <p className="text-xs text-center py-3" style={{ color: txt.muted }}>
              {isActive ? 'Click units on the right to add them here.' : 'Check this group then click units to add.'}
            </p>
          ) : (
            <div className="space-y-1">
              {group.unitIds.map((uid, uIdx) => {
                const unit = unitById(uid);
                if (!unit) return null;
                return (
                  <div key={uid} className="flex items-center gap-2 px-3 py-2 rounded-lg"
                    style={{ backgroundColor: bg.secondary, border: `1px solid ${bdr}` }}>
                    <GripVertical className="w-3.5 h-3.5 shrink-0" style={{ color: txt.muted }} />

                    <span className="text-xs font-mono w-5 text-center" style={{ color: txt.muted }}>
                      {uIdx + 1}
                    </span>

                    <span className="flex-1 text-xs font-medium" style={{ color: txt.primary }}>
                      {unit.number}{' '}
                      <span style={{ color: txt.muted }}>({unit.categoryName})</span>
                    </span>

                    <span className="text-xs" style={{ color: txt.muted }}>
                      {formatCurrency(unit.rent)}
                    </span>

                    {/* move up/down */}
                    <div className="flex flex-col">
                      <button onClick={() => onMoveUnit(uid, 'up')} disabled={uIdx === 0}
                        className="disabled:opacity-25 p-0.5">
                        <ChevronUp className="w-3 h-3" style={{ color: txt.muted }} />
                      </button>
                      <button onClick={() => onMoveUnit(uid, 'down')} disabled={uIdx === group.unitIds.length - 1}
                        className="disabled:opacity-25 p-0.5">
                        <ChevronDown className="w-3 h-3" style={{ color: txt.muted }} />
                      </button>
                    </div>

                    {/* insert at position */}
                    <button onClick={() => onInsertRequest(uid)}
                      className="p-1 rounded transition" title="Insert a unit at this position"
                      style={{ color: Colors.accent.purple }}
                      onMouseEnter={e => e.currentTarget.style.backgroundColor = `${Colors.accent.purple}15`}
                      onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                      <MoveVertical className="w-3.5 h-3.5" />
                    </button>

                    {/* remove */}
                    <button
                      onClick={(e) => { e.stopPropagation(); onRemoveUnit(uid); }}
                      className="p-1 rounded transition"
                      style={{ color: Colors.status.error }}
                      onMouseEnter={e => e.currentTarget.style.backgroundColor = `${Colors.status.error}15`}
                      onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};


// ─────────────────────────────────────────────────────────────────────────────
// InsertPositionDialog
// ─────────────────────────────────────────────────────────────────────────────
const InsertPositionDialog = ({ unitId, unitLabel, groups, onInsert, onClose, bg, txt, bdr, P, Colors }) => {
  const [selectedGroupKey, setSelectedGroupKey] = useState(groups[0]?._key || '');
  const [position, setPosition] = useState(0);

  const selectedGroup = groups.find(g => g._key === selectedGroupKey);
  const maxPos = selectedGroup ? selectedGroup.unitIds.length : 0;

  return createPortal(
    <div className="fixed inset-0 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10000 }}>
      <div className="rounded-xl shadow-2xl w-full max-w-sm"
        style={{ backgroundColor: bg.secondary, border: `1px solid ${bdr}` }}>

        <div className="flex items-center justify-between px-5 py-4"
          style={{ borderBottom: `1px solid ${bdr}`, backgroundColor: bg.tertiary }}>
          <div className="flex items-center gap-2">
            <MoveVertical className="w-4 h-4" style={{ color: Colors.accent.purple }} />
            <h3 className="font-bold text-sm" style={{ color: txt.primary }}>Insert Unit at Position</h3>
          </div>
          <button onClick={onClose} style={{ color: txt.muted }}><X className="w-4 h-4" /></button>
        </div>

        <div className="p-5 space-y-4">
          <div className="px-3 py-2 rounded-lg text-xs font-medium"
            style={{ backgroundColor: `${Colors.accent.purple}12`, color: Colors.accent.purple }}>
            Inserting: <strong>{unitLabel}</strong>
          </div>

          <div>
            <label className="block text-xs font-semibold mb-1.5" style={{ color: txt.secondary }}>
              Into group
            </label>
            <select value={selectedGroupKey}
              onChange={e => { setSelectedGroupKey(e.target.value); setPosition(0); }}
              className="w-full px-3 py-2 rounded-lg text-sm focus:outline-none"
              style={{ backgroundColor: bg.tertiary, color: txt.primary, border: `1px solid ${bdr}` }}>
              {groups.map(g => (
                <option key={g._key} value={g._key}>{g.name || 'Unnamed group'}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold mb-1.5" style={{ color: txt.secondary }}>
              At position (0 = first, {maxPos} = last)
            </label>
            <input type="number" min={0} max={maxPos} value={position}
              onChange={e => setPosition(Math.max(0, Math.min(maxPos, parseInt(e.target.value) || 0)))}
              className="w-full px-3 py-2 rounded-lg text-sm focus:outline-none"
              style={{ backgroundColor: bg.tertiary, color: txt.primary, border: `1px solid ${bdr}` }} />
            {maxPos > 0 && (
              <p className="text-xs mt-1" style={{ color: txt.muted }}>
                Will push the current unit at position {position + 1} down by one.
              </p>
            )}
          </div>
        </div>

        <div className="flex gap-3 px-5 py-4" style={{ borderTop: `1px solid ${bdr}`, backgroundColor: bg.tertiary }}>
          <button onClick={onClose}
            className="flex-1 px-4 py-2 rounded-lg text-sm font-semibold transition"
            style={{ border: `1px solid ${bdr}`, color: txt.primary, backgroundColor: bg.secondary }}>
            Cancel
          </button>
          <button onClick={() => onInsert(unitId, selectedGroupKey, position)}
            className="flex-1 px-4 py-2 rounded-lg text-sm font-semibold text-white transition"
            style={{ backgroundColor: Colors.accent.purple }}>
            Insert
          </button>
        </div>
      </div>
    </div>
  , document.body);
};

export default GroupUnitsDialog;